/**
 * Contrato de `/admin/files` (monolith, `stored-file-admin.dto.ts` e
 * `list-stored-files-query.dto.ts` do módulo `files`). Ver
 * docs/reference/modules/files.md § Gerenciador admin.
 */

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

export type StoredFileStatus = 'active' | 'superseded' | 'deleted' | 'missing';

export const STORED_FILE_STATUS_LABELS: Record<StoredFileStatus, string> = {
  active: 'Ativo',
  superseded: 'Substituído',
  deleted: 'Apagado',
  missing: 'Sumiu do bucket',
};

/**
 * Uma linha do inventário. `name` e `path` repetem o nome que o cliente deu ao
 * upload e podem identificar pessoa (Decisão 7 do épico files-admin): a tela
 * os mostra só no detalhe, nunca na URL, em `title`/`aria-label`, toast ou
 * console.
 */
export interface StoredFile {
  fileId: string;
  module: string;
  tenant: string | null;
  ownerType: string;
  ownerId: string | null;
  /** Chave do registro de buckets do back, não o nome real. */
  bucket: string;
  path: string;
  name: string;
  mime: string;
  sizeBytes: number;
  status: StoredFileStatus;
  inThisStatusSince: string;
  /** `platformProfileId` de quem subiu (o anexo antigo do pipefy guarda o uid). */
  uploadedBy: string | null;
  lastResolvedAt: string | null;
  resolveCount: number;
  legalHold: boolean;
  expiresAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StoredFileDetail extends StoredFile {
  /** `${module}/${ownerType}/${ownerId}` — não é rota do front; ver `owner-link.tsx`. */
  ownerDeepLink: string | null;
}

export interface FilesSummaryRow {
  key: string | null;
  label: string;
  count: number;
  totalBytes: number;
}

/**
 * O par `module` + `ownerType` vem separado para o filtro por dono preencher os
 * dois sem partir a `key` (`<module>/<ownerType>`): o back só filtra o dono com
 * os dois juntos.
 */
export interface FilesOwnerTypeSummaryRow extends FilesSummaryRow {
  key: string;
  module: string;
  ownerType: string;
}

export interface FilesSummary {
  byModule: FilesSummaryRow[];
  byTenant: FilesSummaryRow[];
  byBucket: FilesSummaryRow[];
  byOwnerType: FilesOwnerTypeSummaryRow[];
}

/**
 * Filtros como vivem na URL (`useUrlFilters` só serve string). O `status`
 * abre em `active` (Decisão 6); `all` é o "sem filtro de status", porque valor
 * vazio cairia de volta no default. Tamanho vai em MiB, e a data é `AAAA-MM-DD`.
 * Não há filtro por nome (Decisão 5).
 */
export interface StoredFileFilters {
  bucket: string;
  module: string;
  tenant: string;
  status: StoredFileStatus | 'all';
  minSizeMib: string;
  maxSizeMib: string;
  notResolvedSince: string;
  ownerType: string;
  ownerId: string;
}

export const DEFAULT_STORED_FILE_FILTERS: StoredFileFilters = {
  bucket: '',
  module: '',
  tenant: '',
  status: 'active',
  minSizeMib: '',
  maxSizeMib: '',
  notResolvedSince: '',
  ownerType: '',
  ownerId: '',
};

export interface StoredFilesQuery {
  bucket?: string;
  module?: string;
  tenant?: string;
  status?: StoredFileStatus;
  minSizeBytes?: number;
  maxSizeBytes?: number;
  notResolvedSince?: string;
  ownerType?: string;
  ownerId?: string;
}

const BYTES_PER_MIB = 1024 * 1024;

function mibToBytes(raw: string): number | undefined {
  const mib = Number(raw);
  return raw.trim() !== '' && Number.isFinite(mib) && mib >= 0
    ? Math.round(mib * BYTES_PER_MIB)
    : undefined;
}

/**
 * `AAAA-MM-DD` → início do dia no fuso de quem filtra. A data pura o back lê
 * como meia-noite UTC, três horas antes da meia-noite de Brasília.
 */
function localDayStart(day: string): string | undefined {
  const date = new Date(`${day}T00:00:00`);
  return day && !Number.isNaN(date.getTime()) ? date.toISOString() : undefined;
}

/**
 * Filtro da URL → query do back. O dono só vai com `module` e `ownerType`,
 * como o back exige (o `ownerId` sozinho é 400), e o par incompleto não sai.
 */
export function toStoredFilesQuery(f: StoredFileFilters): StoredFilesQuery {
  const hasOwner = f.module !== '' && f.ownerType.trim() !== '';
  return {
    bucket: f.bucket || undefined,
    module: f.module || undefined,
    tenant: f.tenant || undefined,
    status: f.status === 'all' ? undefined : f.status,
    minSizeBytes: mibToBytes(f.minSizeMib),
    maxSizeBytes: mibToBytes(f.maxSizeMib),
    notResolvedSince: localDayStart(f.notResolvedSince),
    ownerType: hasOwner ? f.ownerType.trim() : undefined,
    ownerId: hasOwner && f.ownerId.trim() ? f.ownerId.trim() : undefined,
  };
}
